import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { accountApi } from "@/shared/services";

export const useValidateUpdateBillingDetails = () => {
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const startTimeRef = useRef<number>(Date.now());
  const handlePaddleEvents = useCallback((event: MessageEvent) => {
    if (event.data.action === "complete") {
      triggerCheck();
    }
  }, []);

  const triggerCheck = useCallback(async () => {
    try {
      const account = await accountApi.getAccount();
      if (account.subscriptionId) {
        if (account.subscriptionStatus !== "past_due") {
          window.localStorage.setItem(
            "updated-billing-details",
            account.subscriptionStatus === "active" ? "true" : "false",
          );
          track("success updaing billing details", {
            "user status": account.subscriptionStatus,
          });
          window.location.reload();
          return;
        }
      }

      if (account.lastPaymentErrorMessage) {
        track("failed to update billing details", {
          "failure message": `lastPaymentErrorMessage: ${account.lastPaymentErrorMessage}`,
        });
        toast.error("Billing details update has failed");
        dispatch(closeDialog("update-billing-details-dialog"));
        return;
      }

      if (Date.now() - startTimeRef.current >= 5 * 60 * 1000) {
        track("failed to update billing details", {
          "failure message": "5 minutes passed with no update",
        });
        toast.error("Billing details update has failed");
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        dispatch(closeDialog("update-billing-details-dialog"));
        return;
      }

      timeoutRef.current = setTimeout(triggerCheck, 1000);
    } catch {
      toast.error("Billing details update has failed");
      dispatch(closeDialog("update-billing-details-dialog"));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      dispatch(closeDialog("update-billing-details-dialog"));
    }
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("message", handlePaddleEvents.bind(this), false);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      window.removeEventListener(
        "message",
        handlePaddleEvents.bind(this),
        false,
      );
    };
  }, [handlePaddleEvents]);
};
